import "../../../src/scss/editorControls.scss";
import { C1_ELO_LIGHT_TYPE_LABELS } from "../C1_uiLabels";
import { itx } from "../../../src/translationItx/itx";
import { useEffect, useLayoutEffect, useRef, useState, type ReactElement } from "react";
import { createPortal } from "react-dom";
import { ChannelType, channelTypeHirarchie } from "../../../src/models/ChannelType";


/** Selects the consumer function and its independent DALI flag in one popup. */
export function C1_ChannelTypeSelect({ value, isDali, options, onChannelTypeChange, onDaliChange }: {
  value: ChannelType;
  isDali: boolean;
  options?: readonly ChannelType[];
  onChannelTypeChange: (value: ChannelType) => void;
  onDaliChange: (value: boolean) => void;
}) {
  const popup = useRef<HTMLDetailsElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ left: number; top: number; width: number; maxHeight: number; openAbove: boolean }>();
  const close = (restoreFocus = false) => {
    if (popup.current) popup.current.open = false;
    setOpen(false);
    if (restoreFocus) popup.current?.querySelector("summary")?.focus();
  };

  useEffect(() => {
    if (!open) return;
    const outsidePointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!popup.current?.contains(target) && !menu.current?.contains(target)) close();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") close(true);
    };
    document.addEventListener("pointerdown", outsidePointer);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outsidePointer);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) { setPosition(undefined); return; }
    const updatePosition = () => {
      const rect = popup.current?.querySelector("summary")?.getBoundingClientRect();
      if (!rect) return;
      const padding = 8;
      const gap = 4;
      const below = window.innerHeight - rect.bottom - padding - gap;
      const above = rect.top - padding - gap;
      const openAbove = below < 280 && above > below;
      const width = Math.min(rect.width, window.innerWidth - padding * 2);
      setPosition({
        left: Math.max(padding, Math.min(rect.left, window.innerWidth - padding - width)),
        top: openAbove ? rect.top - gap : rect.bottom + gap,
        width,
        maxHeight: Math.max(0, Math.min(320, openAbove ? above : below)),
        openAbove,
      });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);
  const label = C1_ELO_LIGHT_TYPE_LABELS[value] ?? value;
  const entries: ReactElement[] = [];
  for (const type of new Set([...(options ?? channelTypeHirarchie("isLight") ?? []), value])) {
    entries.push(<div role="option" tabIndex={0} key={type} aria-selected={type === value}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          event.currentTarget.click();
        } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          const next = event.key === "ArrowDown" ? event.currentTarget.nextElementSibling : event.currentTarget.previousElementSibling;
          (next as HTMLElement | null)?.focus();
        }
      }}
      onClick={() => {
        onChannelTypeChange(type);
        close(true);
      }}>{C1_ELO_LIGHT_TYPE_LABELS[type] ?? type}</div>);
  }
  return <details className="C1_channelTypeSelect" ref={popup}
    onToggle={event => setOpen(event.currentTarget.open)}
    onBlur={(event) => {
      const target = event.relatedTarget as Node | null;
      if (!popup.current?.contains(target) && !menu.current?.contains(target)) close();
    }}
    onKeyDown={(event) => {
      if (event.key === "Escape") {
        close(true);
      }
    }}>
    <summary className="estada-editorControl dockControl" aria-label={itx("c1.ui.Lichttyp", "Lichttyp")}>{isDali ? `DALI · ${label}` : label}</summary>
    {open && typeof document !== "undefined" && createPortal(<div ref={menu}
      className="C1_channelTypePopup C1_channelTypePopup--portal dockTypography"
      style={{ position: "fixed", left: position?.left, top: position?.top, width: position?.width,
        maxHeight: position?.maxHeight, transform: position?.openAbove ? "translateY(-100%)" : undefined,
        visibility: position ? "visible" : "hidden" }}>
      <label className="C1_channelTypeDali"><input type="checkbox" checked={isDali}
        onChange={(event) => onDaliChange(event.target.checked)} />DALI</label>
      <div role="listbox" aria-label={itx("ServerTemplateList.channelType", "Kanaltyp")}>{entries}</div>
    </div>, popup.current?.closest(".C1") ?? document.body)}
  </details>;
}
